import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent,
  Chip, Button, IconButton, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, LinearProgress,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon, Flag as FlagIcon, Savings as SavingsIcon,
  Shield as ShieldIcon, CreditCard as CreditCardIcon,
  Delete as DeleteIcon, Edit as EditIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { colors } from '../styles';
import { getGoals, getGoalsSummary, createGoal, updateGoal, deleteGoal } from '../services/api';

const StyledCard = styled(Card)({
  borderRadius: 14,
  boxShadow: 'none',
  cursor: 'pointer',
  transition: 'transform 0.1s, box-shadow 0.1s',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
});

const GOAL_TYPE_META = {
  savings: { label: 'Savings', icon: <SavingsIcon />, color: colors.primary },
  emergency_fund: { label: 'Emergency Fund', icon: <ShieldIcon />, color: '#4CAF50' },
  debt_payoff: { label: 'Debt Payoff', icon: <CreditCardIcon />, color: '#FF9800' },
  custom: { label: 'Custom', icon: <FlagIcon />, color: '#9C27B0' },
};

const STATUS_COLORS = {
  active: '#4CAF50',
  paused: '#FF9800',
  completed: '#9E9E9E',
  cancelled: '#F44336',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({ name: '', goalType: 'savings', targetAmount: '', targetDate: '', payoffStrategy: 'snowball' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const [goalsRes, summaryRes] = await Promise.all([
        getGoals(params),
        getGoalsSummary(),
      ]);
      if (goalsRes?.data) setGoals(goalsRes.data);
      if (summaryRes?.data) setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to load goals', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateDialog = () => {
    setEditingGoal(null);
    setFormData({ name: '', goalType: 'savings', targetAmount: '', targetDate: '', payoffStrategy: 'snowball' });
    setDialogOpen(true);
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    setFormData({
      name: goal.name,
      goalType: goal.goalType,
      targetAmount: String(goal.targetAmount),
      targetDate: goal.targetDate ? goal.targetDate.split('T')[0] : '',
      payoffStrategy: goal.payoffStrategy || 'snowball',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.targetAmount) return;
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        targetAmount: parseFloat(formData.targetAmount),
        ...(formData.targetDate && { targetDate: formData.targetDate }),
        ...(formData.goalType === 'debt_payoff' && { payoffStrategy: formData.payoffStrategy }),
      };
      if (editingGoal) {
        await updateGoal(editingGoal._id, payload);
      } else {
        await createGoal({ ...payload, goalType: formData.goalType });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to save goal', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await deleteGoal(goalId);
      fetchData();
    } catch (err) {
      console.error('Failed to delete goal', err);
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return '#4CAF50';
    if (progress >= 60) return colors.primary;
    if (progress >= 30) return '#FF9800';
    return '#F44336';
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ color: colors.primary }} />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color={colors.secondary}>
              Goals
            </Typography>
            {summary && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {formatCurrency(summary.totalCurrentBalance)} saved of {formatCurrency(summary.totalTargetAmount)} target
                {summary.unfundedCount > 0 && (
                  <Chip
                    label={`${summary.unfundedCount} unfunded`}
                    size="small"
                    sx={{ ml: 1, backgroundColor: 'rgba(255,152,0,0.12)', color: '#FF9800', fontWeight: 600 }}
                  />
                )}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{ backgroundColor: colors.primary, borderRadius: '50px', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: colors.primary } }}
          >
            Add Goal
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, borderRadius: '20px !important', px: 2, mx: 0.5, border: 'none', fontSize: '0.85rem' } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="completed">Completed</ToggleButton>
            <ToggleButton value="paused">Paused</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Goals Grid */}
        {goals.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <FlagIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No goals yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Set a savings target, build an emergency fund, or plan your debt payoff.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}
              sx={{ mt: 3, backgroundColor: colors.primary, borderRadius: '50px', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: colors.primary } }}
            >
              Create Your First Goal
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {goals.map((goal) => {
              const meta = GOAL_TYPE_META[goal.goalType] || GOAL_TYPE_META.custom;
              const progress = Math.min(100, Math.max(0, goal.progress || 0));
              const progressColor = getProgressColor(progress);
              const isDebt = goal.goalType === 'debt_payoff';

              return (
                <Grid item xs={12} sm={6} md={4} key={goal._id}>
                  <StyledCard onClick={() => openEditDialog(goal)}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${meta.color}14`, mr: 1.5 }}>
                          {React.cloneElement(meta.icon, { sx: { color: meta.color, fontSize: 22 } })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} fontSize={15} noWrap>{goal.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
                        </Box>
                        <Chip
                          label={goal.status}
                          size="small"
                          sx={{ backgroundColor: `${STATUS_COLORS[goal.status] || '#9E9E9E'}14`, color: STATUS_COLORS[goal.status] || '#9E9E9E', fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </Box>

                      {/* Progress */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ flex: 1, height: 6, borderRadius: 3, mr: 1.5, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { backgroundColor: progressColor, borderRadius: 3 } }}
                        />
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
                          {Math.round(progress)}%
                        </Typography>
                      </Box>

                      {/* Amounts */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">{isDebt ? 'Remaining' : 'Saved'}</Typography>
                          <Typography fontWeight={700} fontSize={18} color={colors.secondary}>
                            {formatCurrency(goal.currentBalance)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">{isDebt ? 'Original' : 'Target'}</Typography>
                          <Typography fontWeight={600} color="text.secondary">
                            {formatCurrency(goal.targetAmount)}
                          </Typography>
                        </Box>
                      </Box>

                      {goal.targetDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Target: {new Date(goal.targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      )}

                      {/* Actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 0.5 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(goal); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(goal._id); }} sx={{ color: '#F44336' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {editingGoal ? 'Edit Goal' : 'Create Goal'}
          </DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            {!editingGoal && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Goal Type</InputLabel>
                <Select
                  value={formData.goalType}
                  label="Goal Type"
                  onChange={(e) => setFormData({ ...formData, goalType: e.target.value })}
                >
                  {Object.entries(GOAL_TYPE_META).map(([key, meta]) => (
                    <MenuItem key={key} value={key}>{meta.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Goal Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="e.g., Vacation Fund"
            />
            <TextField
              label="Target Amount ($)"
              fullWidth
              type="number"
              value={formData.targetAmount}
              onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Target Date (optional)"
              fullWidth
              type="date"
              value={formData.targetDate}
              onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            {formData.goalType === 'debt_payoff' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Payoff Strategy</InputLabel>
                <Select
                  value={formData.payoffStrategy}
                  label="Payoff Strategy"
                  onChange={(e) => setFormData({ ...formData, payoffStrategy: e.target.value })}
                >
                  <MenuItem value="snowball">Snowball — smallest balance first</MenuItem>
                  <MenuItem value="avalanche">Avalanche — highest interest first</MenuItem>
                </Select>
              </FormControl>
            )}
            {editingGoal && (
              <Box sx={{ backgroundColor: '#f9f9f9', borderRadius: '12px', p: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Current Balance</Typography>
                  <Typography fontWeight={600}>{formatCurrency(editingGoal.currentBalance)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Progress</Typography>
                  <Typography fontWeight={600} color={colors.primary}>{Math.round(editingGoal.progress || 0)}%</Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formData.name.trim() || !formData.targetAmount}
              sx={{ backgroundColor: colors.primary, borderRadius: '50px', textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { backgroundColor: colors.primary } }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : editingGoal ? 'Save Changes' : 'Create Goal'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
}
